import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { X, Cookie, Shield } from 'lucide-react';

// Animation styles
const slideUpAnimation = {
  animation: 'slideUp 0.3s ease-out forwards'
};

// Add keyframes to document head
if (typeof document !== 'undefined') {
  const styleId = 'cookie-consent-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes slideUp {
        from {
          transform: translateY(100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    personalization: false
  });

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Delay showing banner slightly for better UX
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    } else {
      try {
        const savedPreferences = JSON.parse(consent);
        setPreferences(savedPreferences);
      } catch (e) {
        // Invalid consent data, show banner
        setShowBanner(true);
      }
    }
  }, []);

  const acceptAll = useCallback(() => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      personalization: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    setShowBanner(false);
  }, []);

  const acceptNecessaryOnly = useCallback(() => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      personalization: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(necessaryOnly));
    setPreferences(necessaryOnly);
    setShowBanner(false);
  }, []);

  const savePreferences = useCallback(() => {
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(consentData));
    setShowBanner(false);
    setShowPreferences(false);
  }, [preferences]);

  if (!showBanner) return null;

  return (
    <>
      {/* Main Cookie Banner - Compact Dark Theme */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-50 p-3 bg-slate-900/95 border-t border-slate-700 backdrop-blur-sm shadow-lg"
        style={slideUpAnimation}
        data-testid="cookie-consent-banner"
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-1.5 bg-amber-500/20 rounded-lg shrink-0">
                <Cookie className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <span className="text-sm text-slate-200">
                  We use cookies to enhance your experience.{' '}
                  <button 
                    onClick={() => setShowPreferences(true)}
                    className="text-amber-400 hover:text-amber-300 underline"
                  >
                    Learn more
                  </button>
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-2 shrink-0">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowPreferences(true)}
                className="text-slate-300 border-slate-700 hover:bg-slate-800 hover:text-amber-300 text-xs px-3 py-1 h-8"
                data-testid="cookie-manage-preferences"
              >
                Manage Preferences
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={acceptNecessaryOnly}
                className="text-slate-300 border-slate-700 hover:bg-slate-800 hover:text-amber-300 text-xs px-3 py-1 h-8"
                data-testid="cookie-necessary-only"
              >
                Necessary Only
              </Button>
              <Button 
                size="sm"
                onClick={acceptAll}
                className="bg-amber-600 hover:bg-amber-500 text-slate-950 text-xs px-3 py-1 h-8"
                data-testid="cookie-accept-all"
              >
                Accept All
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Preferences Modal - Dark Theme */}
      {showPreferences && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-amber-400" />
                <h2 className="text-xl font-bold text-slate-100">Cookie Preferences</h2>
              </div>
              <button 
                onClick={() => setShowPreferences(false)}
                className="p-1 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              <p className="text-sm text-slate-400">
                Choose which cookies you want to allow. Your choice will be saved for future visits.
              </p>

              {/* Cookie Categories */}
              <div className="space-y-4">
                {/* Necessary */}
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-slate-100">Necessary Cookies</h3>
                    <span className="text-xs bg-emerald-900/50 text-emerald-400 px-2 py-1 rounded border border-emerald-500/30">Always Active</span>
                  </div>
                  <p className="text-sm text-slate-400">
                    Essential for the website to function properly. These cannot be disabled.
                  </p>
                </div>

                {/* Analytics */}
                <div className="p-4 border border-slate-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-slate-100">Analytics Cookies</h3>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-slate-400 after:border-slate-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600 peer-checked:after:bg-white"></div>
                    </label>
                  </div>
                  <p className="text-sm text-slate-400">
                    Help us understand how visitors interact with our website to improve user experience.
                  </p>
                </div>

                {/* Marketing */}
                <div className="p-4 border border-slate-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-slate-100">Marketing Cookies</h3>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={preferences.marketing}
                        onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-slate-400 after:border-slate-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600 peer-checked:after:bg-white"></div>
                    </label>
                  </div>
                  <p className="text-sm text-slate-400">
                    Used to deliver relevant advertisements and track campaign effectiveness.
                  </p>
                </div>

                {/* Personalization */}
                <div className="p-4 border border-slate-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-slate-100">Personalization Cookies</h3>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={preferences.personalization}
                        onChange={(e) => setPreferences({...preferences, personalization: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-slate-400 after:border-slate-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600 peer-checked:after:bg-white"></div>
                    </label>
                  </div>
                  <p className="text-sm text-slate-400">
                    Remember your preferences and provide a customized experience.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-700 bg-slate-800/50">
              <Button 
                variant="outline"
                onClick={() => setShowPreferences(false)}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button 
                onClick={savePreferences}
                className="bg-amber-600 hover:bg-amber-500 text-slate-950"
              >
                Save Preferences
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
